'use client';

import type { ReactNode } from 'react';
import dynamic from 'next/dynamic';

import { FilterProviderAppRouter } from '@/src/lib/filters';
import { AllFiltersModalProvider } from '@/src/components/ListingSearch/AllFiltersModalProvider';
import { HeaderVisibilityProvider } from '@/src/layouts/HeaderVisibilityContext';
import { SrpPageViewCoordinatorProvider } from '@/src/analytics/SrpPageViewCoordinator';
import { RecentSearchPersistence } from './RecentSearchPersistence';
import { SearchViewModeProvider } from './SearchViewModeContext';
import { SrpSaveSearchProvider } from '../_srp/_components/SrpSaveSearchProvider';
import { usePathname } from '@/src/i18n/navigation';
import { isHdpPathname } from '@/src/lib/utils/listingSlugUtils';
import { AllRentalsSlugProvider } from '@/src/lib/seo/AllRentalsSlugProvider';

const WebMcpFilterTools = dynamic(() => import('./WebMcpFilterTools').then((m) => m.WebMcpFilterTools), {
  ssr: false,
});

// SavedListingsProvider lives in the global Providers (src/app/providers.tsx) — it's
// needed by <ListingCard> on non-resource routes (homepage FeaturedRentals etc.).
// AllFiltersModalProvider hosts the All-Filters modal at this persistent shell level
// so it survives the SRP slug morph (which remounts the page-level search bar).
export function SearchShellProviders({
  children,
  initialHideHeaderOnMobile,
  allRentalsSlug,
}: {
  children: ReactNode;
  initialHideHeaderOnMobile?: boolean;
  allRentalsSlug: string;
}) {
  // The filter tools belong to the results page, not the listing page. Both live in this route
  // group, but on an HDP `apply_rental_filters` would rewrite the listing URL's query string —
  // technically consistent with the shell carrying filters across SRP<->HDP, but a confusing
  // affordance to hand an agent standing on a single listing. Unmounting here unregisters them.
  const pathname = usePathname();
  const showFilterTools = !isHdpPathname(pathname);

  return (
    <AllRentalsSlugProvider slug={allRentalsSlug}>
      <SrpPageViewCoordinatorProvider>
        <FilterProviderAppRouter>
          <RecentSearchPersistence />
          {showFilterTools && <WebMcpFilterTools />}
          <AllFiltersModalProvider>
            <SearchViewModeProvider>
              <HeaderVisibilityProvider initialHideHeaderOnMobile={initialHideHeaderOnMobile}>
                <SrpSaveSearchProvider>{children}</SrpSaveSearchProvider>
              </HeaderVisibilityProvider>
            </SearchViewModeProvider>
          </AllFiltersModalProvider>
        </FilterProviderAppRouter>
      </SrpPageViewCoordinatorProvider>
    </AllRentalsSlugProvider>
  );
}
